// src/components/SpecialOfferPage.js

import React, { useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';
import { motion, AnimatePresence } from 'framer-motion';
import './SpecialOfferPage.css';

// ─── 透過クロスフェードスライダー（alt属性対応版） ───
const CrossfadeSlider = ({ images, alts }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % images.length);
        }, 4000);
        return () => clearInterval(timer);
    }, [images.length]);

    return (
        <div className="akebono-sp-slider-container">
            <AnimatePresence mode="wait">
                <motion.img
                    key={images[currentIndex]}
                    src={images[currentIndex]}
                    alt={alts && alts[currentIndex] ? alts[currentIndex] : `金沢 文化体験 ${currentIndex + 1}`}
                    className="akebono-sp-slider-img"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.5, ease: "easeInOut" }}
                />
            </AnimatePresence>
            <div className="akebono-sp-slider-dots">
                {images.length > 1 && images.map((_, index) => (
                    <span
                        key={index}
                        className={`akebono-sp-slider-dot ${index === currentIndex ? 'active' : ''}`}
                        onClick={() => setCurrentIndex(index)}
                    />
                ))}
            </div>
        </div>
    );
};

const SpecialOfferPage = () => {
    const location = useLocation();
    const isEnglish = location.pathname.startsWith('/en');
    const form = useRef();
    const recaptchaRef = useRef();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [selectedExp, setSelectedExp] = useState('tea');

    // 各種モーダルの状態
    const [isShodoModalOpen, setIsShodoModalOpen] = useState(false);
    const [isKinpakuModalOpen, setIsKinpakuModalOpen] = useState(false);
    const [isYuzenModalOpen, setIsYuzenModalOpen] = useState(false);

    // ▼ 特定のセクションへスクロール
    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const offset = 80;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    // ▼ ダイレクト予約ボタンの処理
    const handleDirectBooking = (expValue) => {
        setSelectedExp(expValue);
        scrollToSection('reservation-form');
    };

    const sendEmail = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const token = recaptchaRef.current.getValue();
        if (!token) {
            setSubmitMessage(isEnglish ? "Please complete the reCAPTCHA." : "reCAPTCHA認証を完了してください。");
            setIsSubmitting(false);
            return;
        }

        const expName = form.current.experience.options[form.current.experience.selectedIndex].text;
        const yuzenPlan = (selectedExp === 'yuzen' || selectedExp === 'multiple') ? form.current.guide_plan.value : 'N/A';

        const combinedMessage = `
〓〓〓 特別体験 ご予約 〓〓〓

【ご希望】 ${expName}
【日時/到着日】 ${form.current.desired_date.value}
【人数/セット数】 ${form.current.participants.value}
${(selectedExp === 'yuzen' || selectedExp === 'multiple') ? `【加賀友禅プラン】 ${yuzenPlan}\n` : ''}
【メッセージ】
${form.current.message.value}
        `.trim();

        emailjs.send(
            'service_nsfmgss',
            'template_mxh45o8',
            {
                user_name: form.current.user_name.value,
                user_email: form.current.user_email.value,
                subject: isEnglish ? `Reservation: ${expName}` : `【ご予約】${expName}`,
                message: combinedMessage,
                'g-recaptcha-response': token,
            },
            process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        )
            .then(() => {
                setSubmitMessage(isEnglish ? 'Sent successfully.' : '送信が完了しました。');
                form.current.reset();
                recaptchaRef.current.reset();
            }, () => {
                setSubmitMessage(isEnglish ? 'Failed to send.' : '送信に失敗しました。');
            })
            .finally(() => setIsSubmitting(false));
    };

    const fadeInProps = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-50px" },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    const content = {
        // ▼ SEO最適化: H1タグ用のタイトルに「金沢」を含める
        pageTitle: isEnglish ? 'Exclusive Cultural Experiences in Kanazawa' : '金沢の伝統に触れる 特別文化体験',
        pageDesc: isEnglish
            ? 'Elevate your stay at Kanazawa Stay Akebono with our handpicked VIP cultural programs. Immerse yourself in the profound traditions of Japan through these private, unforgettable experiences.'
            : '金沢之旅宿 あけぼのでの滞在をさらに特別なものにする、宿泊者様限定のプライベート文化体験をご用意いたしました。金沢が世界に誇る伝統と美に、深く触れるひとときをお過ごしください。',

        tea: {
            value: 'tea',
            title: isEnglish ? 'Private Tea Ceremony' : '一期一会の茶会体験',
            desc: isEnglish
                ? 'Step into a world of tranquility within a historic machiya. Tea master Yuta Takahashi invites you to a private session where you will experience the profound art of the Japanese tea ceremony.'
                : '歴史の息づかいを感じる町家空間へ、金沢で人気の茶道家・髙橋勇太氏をお招きします。あなたのためだけに設えられたプライベートな空間で、ゆったりと流れる時に身を委ねてみませんか。',
            instructorName: isEnglish ? 'Tea Master: Yuta Takahashi' : '亭主：髙橋 勇太',
            price: isEnglish ? '1-2 people: 13,200 JPY / +3,300 JPY per additional guest' : '1〜2名様 13,200円 / 3名様以上 1名につき+3,300円',
            duration: isEnglish ? 'Approx. 1 hour' : '約1時間',
            location: isEnglish ? 'In-house' : '宿内にて実施',
            images: ['/images/tea-1.jpg', '/images/tea-2.jpg', '/images/tea-3.jpg'],
            // ▼ SEO最適化: 画像のalt属性を具体的に
            imageAlts: isEnglish
                ? ['Private Tea Ceremony at Kanazawa Machiya Akebono 1', 'Private Tea Ceremony at Kanazawa Machiya Akebono 2', 'Private Tea Ceremony at Kanazawa Machiya Akebono 3']
                : ['金沢の町家「あけぼの」で体験するプライベート茶道 1', '金沢の町家「あけぼの」で体験するプライベート茶道 2', '金沢の町家「あけぼの」で体験するプライベート茶道 3'],
            linkText: isEnglish ? 'Instructor Instagram ↗' : '講師Instagramを見る ↗',
            linkUrl: 'https://www.instagram.com/tea.base_hakutaku/',
        },
        yuzen: {
            value: 'yuzen',
            title: isEnglish ? 'Kaga Yuzen Silk Dyeing' : '加賀友禅「知る・創る・着る」工房体験',
            desc: isEnglish
                ? 'Step into the private studio of "Maida Sengakougei", a master of Kanazawa\'s traditional Kaga Yuzen dyeing, located just a 1-minute walk from our machiya. Enjoy an exclusive VIP program.'
                : '宿から徒歩1分の現役作家「毎田染画工芸」の工房を貸し切りに。歴史解説から彩色体験、そして加賀友禅の着付けまで、金沢の伝統美にどっぷりと浸る特別な90分をお届けします。',
            instructorName: isEnglish ? 'Studio: Maida Sengakougei' : '工房：毎田染画工芸',
            price: isEnglish ? 'With English Guide: 110,000 JPY / No Guide: 88,000 JPY (Up to 6 guests)' : '英語専属通訳あり: 110,000円 / 通訳なし: 88,000円（1グループ6名様まで）',
            duration: isEnglish ? 'Approx. 90 min' : '約90分',
            location: isEnglish ? 'Maida Sengakougei Studio' : '毎田染画工芸',
            access: isEnglish ? 'Approx. 1 min walk from Akebono' : '宿から徒歩約1分',
            linkText: isEnglish ? 'Official Website ↗' : '公式サイトを見る ↗',
            linkUrl: 'http://www.maida-yuzen.com/',
            btnText: isEnglish ? 'Production Process Details' : '伝統の制作工程・詳細',
            itineraryTitle: isEnglish ? 'The 13 Steps of Kaga Yuzen Production' : '加賀友禅ができるまで（全13工程）',
            itinerarySteps: isEnglish ? [
                { time: 'Step 1', title: 'Pattern Design', desc: 'Creating a full-size design based on the beauty of nature.' },
                { time: 'Step 2', title: 'Temporary Tailoring', desc: 'Sewing white fabric into the shape of a kimono.' },
                { time: 'Step 3', title: 'Transferring Design', desc: 'Tracing the pattern using "Aobana" liquid, which disappears in water.' },
                { time: 'Step 4', title: 'Applying Paste', desc: 'Drawing "Itomenori" outlines using rice or rubber paste to prevent dyes from spreading.' },
                { time: 'Step 5', title: 'Applying Broth', desc: 'Applying soybean broth to the back of the fabric to set the paste.' },
                { time: 'Step 6', title: 'Coloring', desc: 'The heart of production; applying dyes inside the outlines with brushes.' },
                { time: 'Step 7', title: 'Steaming (Fixation)', desc: 'Fixing the colors by applying heat.' },
                { time: 'Step 8', title: 'Protecting Design', desc: 'Covering colored parts with paste to protect them during background dyeing.' },
                { time: 'Step 9', title: 'Dyeing Background', desc: 'Dyeing the entire background of the fabric evenly with a brush.' },
                { time: 'Step 10', title: 'Fabric Steaming', desc: 'Placing the fabric in a steam-filled box for about an hour to permanently set all dyes.' },
                { time: 'Step 11', title: 'Washing (Mizumoto)', desc: 'Washing away excess dye and paste in running water (Yuzen Nagashi).' },
                { time: 'Step 12', title: 'Finishing (Yunoshi)', desc: 'Adjusting fabric weave and width using steam.' },
                { time: 'Step 13', title: 'Final Tailoring', desc: 'Sewing the finished fabric into its final kimono form.' }
            ] : [
                { time: '工程 1', title: '図案作成', desc: '自然の美しさを文様化し、原寸大のデザインを紙に描きます。' },
                { time: '工程 2', title: '仮仕立て', desc: '白生地を一度着物の形に仕立て、寸法を確認します。' },
                { time: '工程 3', title: '下絵', desc: '水で消える「青花」の汁を使い、図案を白生地に写し取ります。' },
                { time: '工程 4', title: '糊置き', desc: '米糊やゴム糊を用いた「糸目糊」で、彩色した色がにじみ出さないよう輪郭を描きます。' },
                { time: '工程 5', title: '地入れ', desc: '大豆の汁（豆汁）を引き、糊の食い込みと染料の定着を助けます。' },
                { time: '工程 6', title: '彩色', desc: '加賀友禅の心臓部。筆を使い、輪郭の内側に色をさしていきます。' },
                { time: '工程 7', title: '下蒸し', desc: '熱をかけることで、彩色した色を生地に定着させます。' },
                { time: '工程 8', title: '中埋め（糊伏せ）', desc: '彩色部分を糊で覆い、地色を染める際に色が混ざるのを防ぎます。' },
                { time: '工程 9', title: '地染め（引き染）', desc: '刷毛を使い、ムラなく生地全体を染め上げます。' },
                { time: '工程 10', title: '本蒸し', desc: '蒸気の箱に入れ約一時間蒸すことで、全ての染色を完全に定着させます。' },
                { time: '工程 11', title: '水洗（水元）', desc: '余分な染料や糊を流水で洗い流します（友禅流し）。' },
                { time: '工程 12', title: '湯のし', desc: '蒸気で生地の目や幅を整え、絹本来の風合いに戻します。' },
                { time: '工程 13', title: '仕立て', desc: '最終的な着物の形に縫い上げ、完成となります。' }
            ],
            itineraryNote: isEnglish
                ? '※ Our program allows you to see these authentic processes at the working studio. All steps are handled consistently in-house, but the specific processes available for viewing depend on the day\'s production schedule.'
                : '※体験プログラムでは、これらの本物の制作現場を間近で見学・体験いただけます。全工程を一貫して行っていますが、見学できる工程は当日の制作状況によって異なります。',
            images: ['/images/maida-studio.jpg'],
            // ▼ SEO最適化: 画像のalt属性
            imageAlts: isEnglish
                ? ['Kaga Yuzen Silk Dyeing Experience at Maida Studio Kanazawa']
                : ['金沢・毎田染画工芸での加賀友禅 彩色・着付け体験'],
            service: isEnglish
                ? <><strong>【Note】</strong><br />As there are no English-speaking staff at the workshop, if you choose the "No Guide" plan, we kindly ask that guests understand basic Japanese or arrange their own interpreter.</>
                : <><strong>【ご予約時の注意事項】</strong><br />事業者様（工房）には英語を話せるスタッフが不在のため、通訳なしプランをご希望の場合は「日本語がある程度理解できる方」または「ご自身で通訳を手配される方」のご利用をお願いしております。</>,
        },
        shodo: {
            value: 'shodo',
            title: isEnglish ? 'Zen Calligraphy' : '自分と向き合う書道体験',
            desc: isEnglish
                ? 'At "Culture Lab Nomachi," immerse yourself in a quiet moment of self-reflection through Shodo. Express meaningful characters with a brush and take home your creation.'
                : '金沢の町家「カルチャーラボ野町」にて、書道を通じて自分自身と向き合う時間。意味を持った文字を筆で表現し、最後は掛け軸や扇子としてお持ち帰りいただけます。',
            instructorName: isEnglish ? 'Instructor: Culture Lab Nomachi' : '講師：カルチャーラボ野町',
            feature: isEnglish ? 'Special: You can hang your finished scroll in the alcove of Akebono during your stay.' : '特典：完成した掛け軸は、滞在中に「あけぼの」の床の間へ掛けてお楽しみいただくことも可能です。',
            price: isEnglish ? '10,000 JPY per person' : '1名様 10,000円（税込）',
            time: '10:00 / 13:00 / 14:30 / 17:30',
            location: isEnglish ? 'Culture Lab Nomachi (20 min walk)' : 'カルチャーラボ野町（宿から徒歩約20分）',
            images: ['/images/shodo-1.jpg', '/images/shodo-2.jpg', '/images/shodo-3.jpg'],
            // ▼ SEO最適化: 画像のalt属性（書道も漏らさず設定）
            imageAlts: isEnglish
                ? ['Zen Calligraphy and Scroll Making at Culture Lab Nomachi Kanazawa 1', 'Zen Calligraphy and Scroll Making at Culture Lab Nomachi Kanazawa 2', 'Zen Calligraphy and Scroll Making at Culture Lab Nomachi Kanazawa 3']
                : ['金沢 カルチャーラボ野町での書道体験・掛け軸制作 1', '金沢 カルチャーラボ野町での書道体験・掛け軸制作 2', '金沢 カルチャーラボ野町での書道体験・掛け軸制作 3'],
            btnText: isEnglish ? 'View Program' : 'プログラム詳細を見る',
            itineraryTitle: isEnglish ? 'Calligraphy Experience Program (60 min)' : '書道体験プログラムの行程（60分）',
            itinerarySteps: isEnglish ? [
                { time: 'Step 1', title: 'Introduction', desc: 'Understanding the background of calligraphy and its spiritual significance.' },
                { time: 'Step 2', title: 'The Basics', desc: 'Learning the proper way to hold the brush, posture, and breathing.' },
                { time: 'Step 3', title: 'Practice & Creation', desc: 'Selecting a meaningful character and expressing it on a hanging scroll or fan.' },
                { time: 'Step 4', title: 'Conclusion', desc: 'Sharing the meaning of your work and concluding the session.' }
            ] : [
                { time: 'Step 1', title: '導入', desc: '書道が持つ精神性や、文字の背景にある思想について学びます。' },
                { time: 'Step 2', title: '基本', desc: '正しい筆の持ち方、姿勢、そして心を整える呼吸法を習得します。' },
                { time: 'Step 3', title: '実践', desc: '自分にふさわしい文字を選定し、掛け軸または扇子に表現します。' },
                { time: 'Step 4', title: '仕上げ', desc: '完成した作品の意味を共有し、体験を締めくくります。' }
            ],
            itineraryNote: isEnglish ? '※ Note: Brushes made from animal hair are used. If you are vegan, please contact us in advance.' : '※本プログラムでは伝統的な動物性の筆を使用しております。'
        },
        kinpaku: {
            value: 'kinpaku',
            title: isEnglish ? 'Gold Leaf Premium Package' : '金箔プレミアムパッケージ',
            desc: isEnglish
                ? 'Experience the brilliance of Kanazawa with "Imai Kinpaku". We offer Akebono guests an exclusive, fully packaged premium experience including crafting and tasting.'
                : '老舗「今井金箔」との特別コラボ。制作から味わうことまで、金沢の美を余すことなく体験できる、あけぼの限定の豪華パッケージです。',
            instructorName: isEnglish ? 'Imai Kinpaku Main Store' : '今井金箔 本店',
            feature: isEnglish ? 'Akebono Exclusive: Includes an edible gold leaf souvenir and a 10% discount on shopping.' : 'あけぼの限定特典：お土産（食用金箔）プレゼント ＆ 当日の店内お買い物が10%OFF',
            price: isEnglish ? '7,700 JPY per person' : '1名様 7,700円（税込）',
            time: '10:30 / 13:00 / 15:00',
            location: isEnglish ? 'Imai Kinpaku Main Store (3 min walk)' : '今井金箔 本店（宿から徒歩約3分）',
            images: ['/images/kinpaku-tumbler.jpg', '/images/kinpaku-badge.jpg', '/images/kinpaku-koban.jpg'],
            // ▼ SEO最適化: 画像のalt属性
            imageAlts: isEnglish
                ? ['Premium Gold Leaf Experience at Imai Kinpaku Kanazawa 1', 'Premium Gold Leaf Experience at Imai Kinpaku Kanazawa 2', 'Premium Gold Leaf Experience at Imai Kinpaku Kanazawa 3']
                : ['金沢 今井金箔での金箔貼りプレミアム体験 1', '金沢 今井金箔での金箔貼りプレミアム体験 2', '金沢 今井金箔での金箔貼りプレミアム体験 3'],
            btnText: isEnglish ? 'View Contents' : 'パッケージ内容を見る',
            itineraryTitle: isEnglish ? 'Gold Leaf Package Contents' : '金箔プレミアムパッケージ内容',
            itinerarySteps: isEnglish ? [
                { time: 'Experience 1', title: 'Gold Leaf Tumbler Making', desc: 'Create your own original tumbler by applying gold leaf to the mount (Approx. 30 min).' },
                { time: 'Experience 2', title: 'Gold Leaf Badge Making', desc: 'Apply gold leaf to a badge. You can wear the finished badge immediately. Perfect as a souvenir (Approx. 10 min).' },
                { time: 'Experience 3', title: 'Gold Leaf Manju Sweets', desc: 'Apply gold leaf to a traditional Manju sweet and enjoy it with Kaga-bocha tea (Approx. 10 min).' },
                { time: 'Bonus 1', title: 'Edible Gold Leaf Souvenir', desc: 'Take home edible gold leaf to enjoy during your stay at Akebono.' },
                { time: 'Bonus 2', title: 'Shopping Discount', desc: 'Enjoy a 10% discount on all purchases made at the store on the day of the experience.' }
            ] : [
                { time: '体験 1', title: '金箔タンブラー制作体験', desc: 'タンブラーの台紙に金箔を貼り、世界に一つだけのオリジナル作品を制作します（所要時間：約30分）。' },
                { time: '体験 2', title: 'バッジ金箔貼り体験', desc: 'バッジに金箔を貼り、完成したものはすぐに付けることが可能です。お土産にもぴったりです（所要時間：約10分）。' },
                { time: '体験 3', title: '金箔饅頭体験「百万石の花小判」', desc: 'お客様自身が金箔を貼って仕上げる和スイーツ。加賀棒茶とともにお召し上がりいただけます（所要時間：約10分）。' },
                { time: '特典 1', title: '食用金箔お土産', desc: '滞在中のあけぼの客室でコーヒーやお茶に使用できる食用金箔をプレゼントいたします。' },
                { time: '特典 2', title: 'お買い物10%OFF', desc: '体験参加者限定で、当日店内での商品購入が10%OFFとなります。' }
            ],
            itineraryNote: isEnglish ? '※ All of the above are included in this one exclusive package.' : '※上記すべてがセットになった、あけぼの限定の豪華な体験パッケージプランです。'
        }
    };

    return (
        <>
            <Helmet>
                {/* ▼ SEO最適化: Titleタグに重要な検索キーワード（金沢、茶道、加賀友禅、書道、金箔）を網羅 */}
                <title>{isEnglish ? 'Exclusive Cultural Experiences in Kanazawa | Kanazawa Stay Akebono' : '金沢の伝統に触れる 特別文化体験（茶道・加賀友禅・書道・金箔）｜金沢之旅宿 あけぼの'}</title>
                <meta
                    name="description"
                    content={isEnglish
                        ? 'Elevate your Kanazawa trip with Akebono\'s exclusive cultural programs. Experience private Tea Ceremonies, Zen Calligraphy, Gold Leaf workshops, and Kaga Yuzen.'
                        : '金沢之旅宿あけぼの限定のプライベート文化体験。お茶会、書道、金箔体験、加賀友禅まで、石川が誇る伝統美に深く触れる特別なひとときをご予約いただけます。'}
                />
                <link rel="alternate" hreflang="ja" href="https://www.kanazawastay-akebono.com/special-offer" />
                <link rel="alternate" hreflang="en" href="https://www.kanazawastay-akebono.com/en/special-offer" />
            </Helmet>

            <div className="akebono-sp-page">
                <div className="akebono-sp-header">
                    {/* ▼ H1タグに反映 */}
                    <h1 className="akebono-sp-main-title">{content.pageTitle}</h1>
                    <p className="akebono-sp-sub-title">{isEnglish ? 'Exclusive Programs' : 'あけぼの限定プログラム'}</p>
                </div>

                {/* クイックナビゲーション（4項目） */}
                <div className="akebono-sp-nav-container">
                    {['tea', 'yuzen', 'shodo', 'kinpaku'].map((key, i) => (
                        <div key={key} className="akebono-sp-nav-card" onClick={() => scrollToSection(`${key}-section`)}>
                            <div className="akebono-sp-nav-bg" style={{ backgroundImage: `url(${content[key].images[0]})` }}></div>
                            <div className="akebono-sp-nav-overlay">
                                <h3>{`0${i + 1}. ${content[key].title.split('（')[0].split('「')[0]}`}</h3>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="akebono-sp-container">
                    <motion.p className="akebono-sp-description" {...fadeInProps}>{content.pageDesc}</motion.p>

                    {/* セクションループ */}
                    {['tea', 'yuzen', 'shodo', 'kinpaku'].map((key, i) => (
                        <motion.div key={key} id={`${key}-section`} className="akebono-sp-section" {...fadeInProps}>
                            <h2 className="akebono-sp-title">{content[key].title}</h2>
                            <div className="akebono-sp-layout akebono-sp-sticky-layout">
                                {/* ▼ SEO最適化: スライダーにalt属性の配列を渡す */}
                                {i % 2 !== 0 && <CrossfadeSlider images={content[key].images} alts={content[key].imageAlts} />}

                                <div className="akebono-sp-content-box akebono-sp-sticky-content">
                                    <h3 className="akebono-sp-instructor-name" style={{ fontSize: '1.2rem', marginBottom: '15px' }}>{content[key].instructorName}</h3>
                                    <p style={{ marginBottom: '15px', lineHeight: '1.8' }}>{content[key].desc}</p>

                                    {content[key].feature && (
                                        <div className="akebono-sp-feature-box">
                                            <strong>{isEnglish ? 'Exclusive:' : '特典:'}</strong> {content[key].feature}
                                        </div>
                                    )}

                                    <ul className="akebono-sp-info-list">
                                        <li><strong>{isEnglish ? 'Price:' : '料金:'}</strong> {content[key].price}</li>
                                        {content[key].duration && <li><strong>{isEnglish ? 'Duration:' : '時間:'}</strong> {content[key].duration}</li>}
                                        {content[key].time && <li><strong>{isEnglish ? 'Time Slots:' : '開始枠:'}</strong> {content[key].time}</li>}
                                        {content[key].location && <li><strong>{isEnglish ? 'Location:' : '場所:'}</strong> {content[key].location}</li>}
                                        {content[key].access && <li><strong>{isEnglish ? 'Access:' : 'アクセス:'}</strong> {content[key].access}</li>}
                                        {content[key].service && <li className="akebono-sp-service-note">{content[key].service}</li>}
                                    </ul>

                                    <div className="akebono-sp-action-btns">
                                        {key === 'yuzen' && <button onClick={() => setIsYuzenModalOpen(true)} className="akebono-sp-btn-outline">{content[key].btnText}</button>}
                                        {key === 'shodo' && <button onClick={() => setIsShodoModalOpen(true)} className="akebono-sp-btn-outline">{content[key].btnText}</button>}
                                        {key === 'kinpaku' && <button onClick={() => setIsKinpakuModalOpen(true)} className="akebono-sp-btn-outline">{content[key].btnText}</button>}
                                        {(key === 'tea' || key === 'yuzen') && <a href={content[key].linkUrl} target="_blank" rel="noopener noreferrer" className="akebono-sp-btn-outline">{content[key].linkText}</a>}

                                        <button
                                            onClick={() => handleDirectBooking(content[key].value)}
                                            className="akebono-sp-btn-solid"
                                        >
                                            {isEnglish ? 'Book this Plan ↓' : 'このプランを申し込む ↓'}
                                        </button>
                                    </div>
                                </div>

                                {/* ▼ SEO最適化: スライダーにalt属性の配列を渡す */}
                                {i % 2 === 0 && <CrossfadeSlider images={content[key].images} alts={content[key].imageAlts} />}
                            </div>
                        </motion.div>
                    ))}

                    {/* 統合フォーム */}
                    <div id="reservation-form" className="akebono-sp-form-section" style={{ scrollMarginTop: '100px' }}>
                        <h2>{isEnglish ? 'Reservation Form' : 'お申し込み・お問い合わせ'}</h2>
                        <form ref={form} onSubmit={sendEmail} className="akebono-form-luxury">
                            <div className="akebono-sp-form-group akebono-sp-highlight">
                                <label>{isEnglish ? 'Selected Experience' : 'ご希望の体験'}</label>
                                <select name="experience" required value={selectedExp} onChange={(e) => setSelectedExp(e.target.value)} style={{ width: '100%', padding: '10px', fontSize: '1rem', border: '1px solid #ccc', marginTop: '5px' }}>
                                    <option value="tea">{isEnglish ? 'Private Tea Ceremony' : 'お茶会体験'}</option>
                                    <option value="yuzen">{isEnglish ? 'Kaga Yuzen Silk Dyeing' : '加賀友禅工房体験'}</option>
                                    <option value="shodo">{isEnglish ? 'Zen Calligraphy' : '書道体験'}</option>
                                    <option value="kinpaku">{isEnglish ? 'Gold Leaf Package' : '金箔プレミアムパッケージ'}</option>
                                    <option value="multiple">{isEnglish ? 'Multiple' : '複数希望'}</option>
                                </select>
                            </div>

                            {/* 加賀友禅専用オプション */}
                            {(selectedExp === 'yuzen' || selectedExp === 'multiple') && (
                                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="akebono-sp-form-group">
                                    <label>{isEnglish ? 'Kaga Yuzen Guide Option' : '加賀友禅 ご希望プラン'} <span className="akebono-sp-req">*</span></label>
                                    <select name="guide_plan" required className="akebono-sp-sub-select" style={{ width: '100%', padding: '10px', marginTop: '5px' }}>
                                        <option value={isEnglish ? 'With Private English Guide (110,000 JPY)' : '英語専属通訳ありプラン (110,000円)'}>{isEnglish ? 'With Private English Guide (110,000 JPY)' : '英語専属通訳ありプラン (110,000円)'}</option>
                                        <option value={isEnglish ? 'Without Guide / Japanese Only (88,000 JPY)' : '通訳なしプラン (88,000円)'}>{isEnglish ? 'Without Guide / Japanese Only (88,000 JPY)' : '通訳なしプラン (88,000円)'}</option>
                                    </select>
                                </motion.div>
                            )}

                            <div className="akebono-sp-form-group"><input type="text" name="user_name" required placeholder={isEnglish ? "Name" : "お名前"} /></div>
                            <div className="akebono-sp-form-group"><input type="email" name="user_email" required placeholder={isEnglish ? "Email" : "メールアドレス"} /></div>
                            <div className="akebono-sp-form-group"><input type="text" name="desired_date" required placeholder={isEnglish ? "Desired Date / Arrival Date" : "希望日時 / 到着日"} /></div>
                            <div className="akebono-sp-form-group"><input type="text" name="participants" required placeholder={isEnglish ? "Participants" : "人数"} /></div>
                            <div className="akebono-sp-form-area"><textarea name="message" rows="4" placeholder={isEnglish ? "Message" : "備考（複数の場合はそれぞれの希望時間を記載）"}></textarea></div>
                            <div className="akebono-sp-recaptcha"><ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} /></div>
                            <button type="submit" disabled={isSubmitting} className="akebono-sp-submit">{isEnglish ? 'Submit' : 'この内容で送信する'}</button>
                        </form>
                        {submitMessage && (
                            <div className={`akebono-sp-msg-box ${submitMessage.includes('Failed') ? 'error' : 'success'}`}>
                                {submitMessage}
                            </div>
                        )}
                    </div>
                </div>

                {/* 行程詳細モーダル群 */}
                {isYuzenModalOpen && (
                    <div className="akebono-sp-modal-overlay" onClick={() => setIsYuzenModalOpen(false)}>
                        <div className="akebono-sp-modal-content" onClick={(e) => e.stopPropagation()}>
                            <button className="akebono-sp-modal-close" onClick={() => setIsYuzenModalOpen(false)}>&times;</button>
                            <h3 className="akebono-sp-modal-title">{content.yuzen.itineraryTitle}</h3>
                            <div className="akebono-sp-timeline">
                                {content.yuzen.itinerarySteps.map((step, index) => (
                                    <div key={index} className="akebono-sp-tl-item">
                                        <div className="akebono-sp-tl-time">{step.time}</div>
                                        <div className="akebono-sp-tl-content">
                                            <h4>{step.title}</h4>
                                            <p>{step.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="akebono-sp-tl-note">
                                <p>{content.yuzen.itineraryNote}</p>
                            </div>
                        </div>
                    </div>
                )}

                {isShodoModalOpen && (
                    <div className="akebono-sp-modal-overlay" onClick={() => setIsShodoModalOpen(false)}>
                        <div className="akebono-sp-modal-content" onClick={(e) => e.stopPropagation()}>
                            <button className="akebono-sp-modal-close" onClick={() => setIsShodoModalOpen(false)}>&times;</button>
                            <h3 className="akebono-sp-modal-title">{content.shodo.itineraryTitle}</h3>
                            <div className="akebono-sp-timeline">
                                {content.shodo.itinerarySteps.map((step, index) => (
                                    <div key={index} className="akebono-sp-tl-item">
                                        <div className="akebono-sp-tl-time">{step.time}</div>
                                        <div className="akebono-sp-tl-content">
                                            <h4>{step.title}</h4>
                                            <p>{step.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="akebono-sp-tl-note">
                                <p>{content.shodo.itineraryNote}</p>
                            </div>
                        </div>
                    </div>
                )}

                {isKinpakuModalOpen && (
                    <div className="akebono-sp-modal-overlay" onClick={() => setIsKinpakuModalOpen(false)}>
                        <div className="akebono-sp-modal-content" onClick={(e) => e.stopPropagation()}>
                            <button className="akebono-sp-modal-close" onClick={() => setIsKinpakuModalOpen(false)}>&times;</button>
                            <h3 className="akebono-sp-modal-title">{content.kinpaku.itineraryTitle}</h3>
                            <div className="akebono-sp-timeline">
                                {content.kinpaku.itinerarySteps.map((step, index) => (
                                    <div key={index} className="akebono-sp-tl-item">
                                        <div className="akebono-sp-tl-time">{step.time}</div>
                                        <div className="akebono-sp-tl-content">
                                            <h4>{step.title}</h4>
                                            <p>{step.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="akebono-sp-tl-note">
                                <p>{content.kinpaku.itineraryNote}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default SpecialOfferPage;